import { formatifyHtml } from "./formatifyHtml.js";
import { thumbFromUrl } from "./thumbFromUrl.js";

/** Šířka sloupce článku (`.article` je `lg:max-w-3xl`); menší varianta se roztahovala a rozmazávala. */
const articleColumnWidth = 768;
const mobileWidth = 530;

/** Exportované kvůli testu — na rozdíl od `transformCkEditorContent` nepotřebuje DOM. */
export function convertImagesToPicture(html: string, lightboxId = "content"): string {
	return html.replace(
		/<img(?<p1>[^>]+)src="(?<src>[^"]+)"(?<p2>[^>]*)>/gu,
		(_match, p1: string, src: string, p2: string) => {
			// Malé obrázky (avataři u citací) nesou width — nemá smysl jim stahovat celý sloupec.
			const declaredWidth = /\bwidth="(?<width>\d+)"/u.exec(`${p1}${p2}`)?.groups?.width;
			const width = declaredWidth ? Math.min(Number(declaredWidth), articleColumnWidth) : articleColumnWidth;
			const narrowWidth = Math.min(width, mobileWidth);
			const image = `<img${p1}src="${thumbFromUrl(width, 0, src)}"${p2}>`;
			// <img> musí být přímý potomek <picture>, jinak prohlížeč <source> ignoruje
			// a stáhne jen to, co je v src — tedy jednonásobek i na retině. Odkaz do
			// lightboxu proto obaluje celé <picture>, ne obrázek uvnitř něj.
			const picture = `
				<picture class="js-picture">
					<source
						srcset="${thumbFromUrl(width, 0, src)} 1x, ${thumbFromUrl(width * 2, 0, src)} 2x"
						type="image/webp"
						media="(min-width: 577px)"
					/>
					<source
						srcset="${thumbFromUrl(narrowWidth, 0, src)} 1x, ${thumbFromUrl(narrowWidth * 2, 0, src)} 2x"
						type="image/webp"
						media="(max-width: 576px)"
					/>
					${image}
				</picture>
			`;
			// Obrázky označené data-no-lightbox (např. avataři u citací) zůstávají neklikací.
			const noLightbox = /\bdata-no-lightbox\b/u.test(`${p1}${p2}`);
			return noLightbox
				? picture
				: `<a href="${thumbFromUrl(1440, 0, src)}" data-fslightbox="${lightboxId}" data-type="image">
						${picture}
					</a>`;
		},
	);
}

/**
 * Tables with a header and at most this many columns collapse into stacked cards on mobile;
 * wider tables (or headerless ones) fall back to whole-surface horizontal scrolling instead.
 */
const MAX_STACK_COLUMNS = 4;

/**
 * Body cells whose text is at most this long keep on a single line (numbers, short
 * labels like "1–2 týdny" or "30 000 Kč"), so wide tables read cleanly. Longer text
 * is allowed to wrap so a single big paragraph can't blow the table out horizontally.
 */
const MAX_NOWRAP_CHARS = 25;

function getColumnCount(table: HTMLTableElement): number {
	let max = 0;
	for (const row of Array.from(table.rows)) {
		let count = 0;
		for (const cell of Array.from(row.cells)) {
			count += cell.colSpan || 1;
		}
		max = Math.max(max, count);
	}
	return max;
}

/** Header labels expanded by colSpan so they align with body columns by index. */
function getHeaderLabels(table: HTMLTableElement): string[] {
	const headerRow = table.tHead?.rows[0];
	if (!headerRow) {
		return [];
	}
	const labels: string[] = [];
	for (const cell of Array.from(headerRow.cells)) {
		const text = (cell.textContent ?? "").trim();
		for (let span = cell.colSpan || 1; span > 0; span--) {
			labels.push(text);
		}
	}
	return labels;
}

/** Adds data-label (column header text) to each body cell so cards can render labels on mobile. */
function injectDataLabels(table: HTMLTableElement, labels: string[]): void {
	for (const body of Array.from(table.tBodies)) {
		for (const row of Array.from(body.rows)) {
			let column = 0;
			for (const cell of Array.from(row.cells)) {
				const label = labels[column];
				if (cell.tagName === "TD" && label) {
					cell.setAttribute("data-label", label);
				}
				column += cell.colSpan || 1;
			}
		}
	}
}

/** Keeps short, label-like body cells on one line while letting long text wrap. */
function markNoWrapCells(table: HTMLTableElement): void {
	for (const body of Array.from(table.tBodies)) {
		for (const row of Array.from(body.rows)) {
			for (const cell of Array.from(row.cells)) {
				if (cell.tagName !== "TD") {
					continue;
				}
				const text = (cell.textContent ?? "").trim();
				const hasLineBreak = cell.querySelector("br") !== null;
				if (text.length > 0 && text.length <= MAX_NOWRAP_CHARS && !hasLineBreak) {
					cell.classList.add("responsive-table__nowrap");
				}
			}
		}
	}
}

function processTables(root: HTMLElement): void {
	for (const table of Array.from(root.querySelectorAll("table"))) {
		const labels = getHeaderLabels(table);
		const hasUsableHeader = labels.some((label) => label.length > 0);
		const stack = hasUsableHeader && getColumnCount(table) <= MAX_STACK_COLUMNS;

		if (stack) {
			injectDataLabels(table, labels);
		}

		markNoWrapCells(table);

		const wrapper = document.createElement("div");
		wrapper.className = `responsive-table ${stack ? "responsive-table--stack" : "responsive-table--scroll"}`;
		table.parentNode?.insertBefore(wrapper, table);
		wrapper.appendChild(table);
	}
}

/** Externí odkazy (mimo fastest.cz) se otevírají v novém tabu; lightboxové odkazy na obrázky se přeskakují. */
function markExternalLinks(root: HTMLElement): void {
	for (const link of Array.from(root.querySelectorAll("a[href]"))) {
		if (link.hasAttribute("data-fslightbox")) {
			continue;
		}
		const href = link.getAttribute("href") ?? "";
		if (!/^https?:\/\//iu.test(href)) {
			continue;
		}
		try {
			const { hostname } = new URL(href);
			const isInternal = hostname === "fastest.cz" || hostname.endsWith(".fastest.cz");
			if (!isInternal) {
				link.setAttribute("target", "_blank");
				link.setAttribute("rel", "noopener noreferrer");
			}
		} catch {
			// Neplatné URL necháváme být.
		}
	}
}

function fixYoutubeIframes(html: string): string {
	return html.replace(
		/<iframe(?<p1>[^>]+)src="https:[/][/]www[.]youtube[.]com[/]embed[/](?<v>[^"]+)"(?<p2>[^>]*)>/gu,
		(_match, p1: string, v: string, p2: string) =>
			`<iframe ${p1} title="YouTube video" src="https://www.youtube.com/embed/${v}" referrerpolicy="strict-origin-when-cross-origin" ${p2}>`,
	);
}

export function transformCkEditorContent(
	html: string,
	options?: { customClass?: string; lightboxId?: string },
): string {
	const { customClass, lightboxId } = options ?? {};
	const withConvertedImages = convertImagesToPicture(html, lightboxId);
	const withFixedYoutubeIframes = fixYoutubeIframes(withConvertedImages);
	const htmlElement = document.createElement("div");
	htmlElement.innerHTML = withFixedYoutubeIframes;
	markExternalLinks(htmlElement);
	processTables(htmlElement);
	const withFormatted = formatifyHtml(htmlElement);
	return `<div class="ckeditor-content ${customClass ?? ""}">${withFormatted}</div>`;
}
